import { useState, type FormEvent } from "react";
import { CheckCircle2, ExternalLink, LoaderCircle, Upload } from "lucide-react";
import { portalApi, type PortalApiClient } from "../api/client";
import { EmptyState, ErrorState, Field, LoadingState, PageHeader } from "../components/Shared";
import { useAsyncData } from "../hooks/useAsyncData";
import type { PublicationJob } from "../types";

export function UploadItem({
  job,
  onPublish,
  saving,
}: {
  job: PublicationJob;
  onPublish(jobId: string, youtubeUrl: string): Promise<void>;
  saving: boolean;
}) {
  const [youtubeUrl, setYoutubeUrl] = useState("");
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    try {
      const parsed = new URL(youtubeUrl);
      if (!["youtube.com", "www.youtube.com", "youtu.be"].includes(parsed.hostname)) {
        throw new Error("Enter a YouTube link.");
      }
    } catch {
      setError("Enter a valid YouTube video link.");
      return;
    }
    setError(null);
    await onPublish(job.id, youtubeUrl);
  }

  return (
    <article className="upload-row">
      <div className="upload-video">
        <span className="channel-label">{job.channelName}</span>
        <strong>{job.title}</strong>
        <span>{job.durationMinutes} minute target</span>
      </div>
      <div className="upload-drive">
        {job.driveUrl ? (
          <a className="button secondary" href={job.driveUrl} target="_blank" rel="noreferrer">
            <ExternalLink size={16} />
            Open finished video
          </a>
        ) : (
          <span className="missing-link">Drive link is still being prepared</span>
        )}
      </div>
      <form className="publication-form" onSubmit={submit}>
        <Field label="YouTube URL" error={error ?? undefined}>
          <input
            type="url"
            required
            placeholder="https://youtube.com/watch?v=..."
            value={youtubeUrl}
            onChange={(event) => setYoutubeUrl(event.target.value)}
          />
        </Field>
        <button className="button primary" type="submit" disabled={saving || !job.driveUrl}>
          {saving ? <LoaderCircle className="spin" size={16} /> : <CheckCircle2 size={16} />}
          Mark Published
        </button>
      </form>
    </article>
  );
}

export function UploadsPage({ client = portalApi }: { client?: PortalApiClient }) {
  const ready = useAsyncData(() => client.publications.ready(), [client]);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  async function publish(jobId: string, youtubeUrl: string) {
    setSavingId(jobId);
    setActionError(null);
    try {
      await client.publications.publish(jobId, youtubeUrl);
      await ready.reload();
    } catch (requestError) {
      setActionError(requestError instanceof Error ? requestError.message : "Publication could not be saved.");
    } finally {
      setSavingId(null);
    }
  }

  if (ready.loading) return <LoadingState label="Loading finished videos" />;
  if (ready.error || !ready.data) {
    return <ErrorState message={ready.error ?? "Finished videos are unavailable."} onRetry={ready.reload} />;
  }

  return (
    <>
      <PageHeader
        title="Ready to Upload"
        description="Open the finished video, upload it to YouTube, then record the final link."
      />
      {actionError ? (
        <div className="inline-alert" role="alert">
          {actionError}
        </div>
      ) : null}
      {ready.data.length ? (
        <section className="upload-list" aria-label="Videos ready to upload">
          {ready.data.map((job) => (
            <UploadItem
              key={job.id}
              job={job}
              saving={savingId === job.id}
              onPublish={publish}
            />
          ))}
        </section>
      ) : (
        <EmptyState
          title="Nothing is waiting for upload"
          message="Finished videos will appear here with their Drive links."
          action={<Upload size={20} aria-hidden="true" />}
        />
      )}
    </>
  );
}
